import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DatePipe, DecimalPipe } from '@angular/common';

import { ToastrModule } from 'ngx-toastr';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';

import { TabMenuModule } from 'primeng/tabmenu';
import { MenubarModule } from 'primeng/menubar';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

/** Lara defaults primary to emerald (green); align with --primary-color (#3b82f6 / #60a5fa). */
const EapsPreset = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

import {
  MsalModule,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService
} from '@azure/msal-angular';

import {
  InteractionType,
  PublicClientApplication
} from '@azure/msal-browser';

import { loginRequest, msalConfig } from './app-msal-config';

import { AppAuthInterceptor } from './app-auth.interceptor';
import { EAPSTranslateLoader } from './langs';

import { AppRoutingModule } from './app-routing.module';
import { DataModule } from './data';
import { IdbSharedModule } from './shared/shared.module';

export function MSALInstanceFactory() {
  return new PublicClientApplication(msalConfig);
}

export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest,
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    DatePipe,
    DecimalPipe,

    importProvidersFrom(
      BrowserAnimationsModule,
      HttpClientModule,

      // Angular modules legacy
      AppRoutingModule,
      DataModule,
      IdbSharedModule,

      // PrimeNG modules
      TabMenuModule,
      MenubarModule,

      // Toastr
      ToastrModule.forRoot({
        enableHtml: true,
        timeOut: 3000,
        progressBar: true,
        positionClass: 'toast-bottom-right'
      }),

      // ngx-translate
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useClass: EAPSTranslateLoader,
        },
      }),

      // MSAL module
      MsalModule
    ),

    providePrimeNG({
      theme: {
        preset: EapsPreset,
        options: {
          darkModeSelector: '.dark-mode',
        }
      }
    }),

    // MSAL providers
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    MsalService,
    MsalBroadcastService,
    MsalGuard,

    // HTTP interceptor
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppAuthInterceptor,
      multi: true,
    },
  ]
};